import type { Education, Experience } from "@/types/cv";

/**
 * Formats a date from timestamp to Spanish localized format
 * @param date - Date timestamp in seconds or milliseconds, string representation of timestamp, or null
 * @returns Formatted date string or empty string if invalid
 */
export function formatDate(date: string | number | null): string {
  if (!date) {
    return "";
  }

  // Convert to number if it's a string
  const timestamp = typeof date === "string" ? Number(date) : date;

  if (typeof timestamp !== "number") {
    return "";
  }

  try {
    // If timestamp looks like seconds (10 digits), convert to milliseconds
    // Unix timestamps in seconds are typically 10 digits (e.g., 1701406800)
    // Unix timestamps in milliseconds are typically 13 digits
    const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;

    const dateObj = new Date(timestampMs);

    if (typeof dateObj.getTime !== "function") {
      return "";
    }
    return dateObj.toLocaleString("es-ES", { year: "numeric", month: "long", timeZone: "UTC" });
  } catch {
    return "";
  }
}

/**
 * Formats education date range for CV display
 * @param edu - Education object with startDate, endDate, and currentlyStudyHere properties
 * @returns Formatted date range string
 */
export function formatEducationDates(edu: Education): string {
  const start = formatDate(edu.startDate);
  const end = edu.currentlyStudyHere ? "Presente" : formatDate(edu.endDate);
  return `${start} - ${end}`;
}

/**
 * Formats experience date range for CV display
 * @param exp - Experience object with startDate, endDate, and currentlyWorkHere properties
 * @returns Formatted date range string
 */
export function formatExperienceDates(exp: Experience): string {
  const start = formatDate(exp.startDate);
  const end = exp.currentlyWorkHere ? "Presente" : formatDate(exp.endDate);
  return `${start} - ${end}`;
}

export function formatPlanRenovationDate(date: string): string {
  const dateObj = new Date(date);
  return dateObj.toLocaleString("es-ES", { month: "numeric", day: "numeric", timeZone: "UTC" });
}

export function formatDateLatam(date: string | number): string {
  const dateObj = new Date(date);
  if (Number.isNaN(dateObj.getTime())) {
    return "";
  }
  return dateObj.toLocaleString("es-ES", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function formatFullDate(date: string | undefined, locale = "es-ES"): string {
  if (!date) {
    return "";
  }

  const dateObj = new Date(date);

  if (Number.isNaN(dateObj.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(dateObj);
}